import { useContext } from 'react';
import { useAccounts } from '../../../../contexts/AccountsContext';
import { isClosed } from 'src/models/account';
import { useAuth0 } from '@auth0/auth0-react';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CategoryIcon from '@mui/icons-material/Category';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { ListSubheader, alpha, Box, List, styled, Button, ListItem, Divider, useTheme } from '@mui/material';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';
import { SidebarContext } from 'src/contexts/SidebarContext';
import { useAdmin } from 'src/contexts/AdminContext';
import DashboardTwoToneIcon from '@mui/icons-material/DashboardTwoTone';
import Science from '@mui/icons-material/Science';
import { posts } from '../../../../content/blog/services/posts';
import { toSeoFriendlyUrl } from '../../../../content/blog/utils/urlHelpers';
import { Post } from '../../../../content/blog/types/Post';

interface SeriesGroup {
  name: string;
  posts: Post[];
}

// Get unique categories and tags from posts
const categories = [...new Set(posts.map(post => post.category))].filter(Boolean);
const tags = [...new Set(posts.flatMap(post => post.tags))].filter(Boolean).sort();

// Get unique series
const series = posts
  .filter(post => post.series)
  .reduce<Record<string, SeriesGroup>>((acc, post) => {
    if (!post.series) return acc;
    if (!acc[post.series.name]) {
      acc[post.series.name] = {
        name: post.series.name,
        posts: []
      };
    }
    acc[post.series.name].posts.push(post);
    return acc;
  }, {});

const MenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {
      padding: ${theme.spacing(1)};

      & > .MuiList-root {
        padding: 0 ${theme.spacing(0)} ${theme.spacing(1)};
      }
    }

    .MuiListSubheader-root {
      text-transform: uppercase;
      font-weight: bold;
      font-size: ${theme.typography.pxToRem(12)};
      color: ${theme.colors.alpha.trueWhite[50]};
      padding: ${theme.spacing(0, 2.5)};
      line-height: 1.4;
    }
`
);

const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {
      .MuiListItem-root {
        padding: 1px 0;

        .MuiBadge-root {
          position: absolute;
          right: ${theme.spacing(3.2)};

          .MuiBadge-standard {
            background: ${theme.colors.primary.main};
            font-size: ${theme.typography.pxToRem(10)};
            font-weight: bold;
            text-transform: uppercase;
            color: ${theme.palette.primary.contrastText};
          }
        }
    
        .MuiButton-root {
          display: flex;
          color: ${theme.colors.alpha.trueWhite[70]};
          background-color: transparent;
          width: 100%;
          justify-content: flex-start;
          padding: ${theme.spacing(1.2, 3)};

          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(['color'])};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
            }
          }

          .MuiButton-startIcon {
            color: ${theme.colors.alpha.trueWhite[30]};
            font-size: ${theme.typography.pxToRem(20)};
            margin-right: ${theme.spacing(1)};
          }
          
          .MuiButton-endIcon {
            color: ${theme.colors.alpha.trueWhite[50]};
            margin-left: auto;
            opacity: .8;
            font-size: ${theme.typography.pxToRem(20)};
          }

          &.active,
          &:hover {
            background-color: ${alpha(theme.colors.alpha.trueWhite[100], 0.06)};
            color: ${theme.colors.alpha.trueWhite[100]};

            .MuiButton-startIcon,
            .MuiButton-endIcon {
              color: ${theme.colors.alpha.trueWhite[100]};
            }
          }
        }

        &.Mui-children {
          flex-direction: column;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(7)};
          }
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            padding: ${theme.spacing(1, 0)};
          }

          .MuiListItem-root {
            padding: 1px 0;

            .MuiButton-root {
              padding: ${theme.spacing(0.8, 3)};

              .MuiBadge-root {
                right: ${theme.spacing(3.2)};
              }

              &:before {
                content: ' ';
                background: ${theme.colors.alpha.trueWhite[100]};
                opacity: 0;
                transition: ${theme.transitions.create([
                  'transform',
                  'opacity'
                ])};
                width: 6px;
                height: 6px;
                transform: scale(0);
                transform-origin: center;
                border-radius: 20px;
                margin-right: ${theme.spacing(1.8)};
              }

              &.active,
              &:hover {

                &:before {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            }
          }
        }
      }
    }
`
);

export const SidebarMenu: React.FC = () => {
  const { closeSidebar } = useContext(SidebarContext);
  const { accounts } = useAccounts();
  const { isAuthenticated } = useAuth0();
  const { isAdmin } = useAdmin();
  const theme = useTheme();
  const location = useLocation();
  const isInBlogSection = location.pathname.startsWith('/blog');

  return (
    <>
      <MenuWrapper>
        <List component="div">
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/blog"
                  startIcon={<MenuBookIcon />}
                >
                  Learn
                </Button>
              </ListItem>
              {isAuthenticated && (
                <>
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/dashboard"
                      startIcon={<DashboardTwoToneIcon />}
                    >
                      Dashboard
                    </Button>
                  </ListItem>
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/simulation-runner"
                      startIcon={<Science />}
                    >
                      Simulation Runner
                    </Button>
                  </ListItem>
                </>
              )}
            </List>
          </SubMenuWrapper>
        </List>

        {isAuthenticated && (
          <>
            <Divider sx={{ background: theme.colors.alpha.trueWhite[10] }} />
            <List
              component="div"
              subheader={
                <ListSubheader component="div" disableSticky>
                  My Accounts
                </ListSubheader>
              }
            >
              <SubMenuWrapper>
                <List component="div">
                  {accounts.filter(account => !isClosed(account)).map((account) => (
                    <ListItem component="div" key={account.id}>
                      <Button
                        disableRipple
                        component={RouterLink}
                        onClick={closeSidebar}
                        to={`/accounts/${account.id}`}
                        startIcon={<AccountBalanceWalletIcon />}
                      >
                        {account.name}
                      </Button>
                    </ListItem>
                  ))}
                </List>
              </SubMenuWrapper>
            </List>
          </>
        )}

        {isInBlogSection && (
          <>
            <Divider sx={{ background: theme.colors.alpha.trueWhite[10] }} />
            <List
              component="div"
              subheader={
                <ListSubheader component="div" disableSticky>
                  Categories
                </ListSubheader>
              }
            >
              <SubMenuWrapper>
                <List component="div">
                  {categories.map((category) => (
                    <ListItem component="div" key={category}>
                      <Button
                        disableRipple
                        component={RouterLink}
                        onClick={closeSidebar}
                        to={`/blog/category/${toSeoFriendlyUrl(category)}`}
                        startIcon={<CategoryIcon />}
                      >
                        {category}
                      </Button>
                    </ListItem>
                  ))}
                </List>
              </SubMenuWrapper>
            </List>

            <Divider sx={{ background: theme.colors.alpha.trueWhite[10] }} />
            <List
              component="div"
              subheader={
                <ListSubheader component="div" disableSticky>
                  Tags
                </ListSubheader>
              }
            >
              <SubMenuWrapper>
                <List component="div">
                  {tags.map((tag) => (
                    <ListItem component="div" key={tag}>
                      <Button
                        disableRipple
                        component={RouterLink}
                        onClick={closeSidebar}
                        to={`/blog/tag/${toSeoFriendlyUrl(tag)}`}
                        startIcon={<LocalOfferIcon />}
                      >
                        {tag}
                      </Button>
                    </ListItem>
                  ))}
                </List>
              </SubMenuWrapper>
            </List>

            {Object.keys(series).length > 0 && (
              <>
                <Divider sx={{ background: theme.colors.alpha.trueWhite[10] }} />
                <List
                  component="div"
                  subheader={
                    <ListSubheader component="div" disableSticky>
                      Series
                    </ListSubheader>
                  }
                >
                  <SubMenuWrapper>
                    <List component="div">
                      {Object.values(series).map((seriesGroup: SeriesGroup) => {
                        const sortedPosts = seriesGroup.posts.sort((a, b) => 
                          (a.series?.order || 0) - (b.series?.order || 0)
                        );
                        const firstPost = sortedPosts[0];

                        return (
                          <ListItem component="div" key={seriesGroup.name}>
                            <Button
                              disableRipple
                              component={RouterLink}
                              onClick={closeSidebar}
                              to={`/blog/post/${firstPost.slug}`}
                              startIcon={<CollectionsBookmarkIcon />}
                            >
                              {seriesGroup.name}
                            </Button>
                          </ListItem>
                        );
                      })}
                    </List>
                  </SubMenuWrapper>
                </List>
              </>
            )}
          </>
        )}

        <Divider />
        {isAdmin && (
          <List component="div">
            <SubMenuWrapper>
              <List component="div">
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/admin"
                    startIcon={<AdminPanelSettingsIcon />}
                  >
                    Admin Settings
                  </Button>
                </ListItem>
              </List>
            </SubMenuWrapper>
          </List>
        )}
        <List component="div">
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/settings"
                  startIcon={<SettingsIcon />}
                >
                  Settings
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>
      </MenuWrapper>
    </>
  );
};
